import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import walletService from '../services/walletService';
import websocketService from '../services/websocketService';
import toast from 'react-hot-toast';
import TournamentLiveEvents from '../components/TournamentLiveEvents/TournamentLiveEvents';
import PlayersTable from '../components/common/PlayersTable';
// Custom layout for LiveBetting page
import './LiveBetting.css';

const LiveBetting = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [tournaments, setTournaments] = useState([]);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [availablePlayers, setAvailablePlayers] = useState([]);
  const [walletBalance, setWalletBalance] = useState(0);
  const [tierSettings, setTierSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [purchaseLoading, setPurchaseLoading] = useState(null);
  const [isWebSocketConnected, setIsWebSocketConnected] = useState(false);
  const [isTournamentStarted, setIsTournamentStarted] = useState(false);
  const showAddFundsButton = false;

  useEffect(() => {
    fetchActiveTournaments();
    fetchWalletBalance();
    fetchTierSettings();
  }, []);

  // Utility function to check if tournament has started or is live
  const checkTournamentStarted = (tournament) => {
    if (!tournament) return false;
    
    // Check if tournament is LIVE (highest priority)
    if (tournament.status === 'LIVE') {
      return true;
    }
    return false;
    // Check if tournament has started by date
    if (tournament.startDate) {
      const now = new Date();
      const startDate = new Date(tournament.startDate);
      return now >= startDate;
    }
    
    return false;
  };

  useEffect(() => {
    if (selectedTournament) {
      fetchAvailablePlayers(selectedTournament.id);
      setIsTournamentStarted(checkTournamentStarted(selectedTournament));
    }
  }, [selectedTournament]);

  // Periodic check to update tournament status (every 30 seconds)
  useEffect(() => {
    if (selectedTournament) {
      const interval = setInterval(() => {
        // Re-fetch tournament data to get updated status
        fetchActiveTournaments().then(() => {
          const hasStarted = checkTournamentStarted(selectedTournament);
          setIsTournamentStarted(hasStarted);
        });
      }, 30000); // Check every 30 seconds

      return () => clearInterval(interval);
    }
  }, [selectedTournament]);

  // WebSocket event handlers
  const handleWalletBalanceUpdated = useCallback((data) => {
    console.log('💰 Wallet balance updated:', data);
    
    // Extract the actual data from the WebSocket message structure
    const eventData = data.data || data;
    
    // Only update if it's for the current user
    if (eventData.userId === user?.id) {
      setWalletBalance(eventData.newBalance);
      
      // Show notification for wallet updates
      toast.success(`Wallet updated: $${eventData.newBalance.toFixed(2)}`, {
        duration: 3000,
        icon: '💰'
      });
    }
  }, [user?.id]);

  const handlePlayerAvailabilityUpdated = useCallback((data) => {
    console.log('👤 Player availability updated:', data);
    
    // Extract the actual data from the WebSocket message structure
    const eventData = data.data || data;
    
    // If player is no longer available, remove from the list
    if (!eventData.isAvailable) {
      setAvailablePlayers(prev => 
        prev.filter(player => player.golfer.id !== eventData.playerId)
      );
      
      // Show notification
      toast.success(`Player is no longer available for purchase`, {
        duration: 3000,
        icon: '👤'
      });
    } else {
      // If player becomes available again, add back to the list
      // This would require fetching the player data from the API
      console.log('Player became available again - would need to fetch player data');
    }
  }, []);

  const handleTournamentInfoUpdated = useCallback((data) => {
    console.log('🏆 Tournament info updated:', data);
    
    // Extract the actual data from the WebSocket message structure
    const eventData = data.data || data;
    
    // Import tournamentLiveService to update tournament info
    import('../services/tournamentLiveService').then(({ default: tournamentLiveService }) => {
      tournamentLiveService.updateTournamentInfo(eventData);
    });
  }, []);

  const handleLeaderboardUpdated = useCallback((data) => {
    console.log('📊 Leaderboard updated:', data);
    
    // Extract the actual data from the WebSocket message structure
    const eventData = data.data || data;
    console.log('📊 Extracted event data:', eventData);
    
    // Update tournament live service with leaderboard data
    import('../services/tournamentLiveService').then(({ default: tournamentLiveService }) => {
      tournamentLiveService.updateTournamentInfo(eventData);
    });
  }, []);

  const handlePurchaseConfirmed = useCallback((data) => {
    console.log('✅ Purchase confirmed:', data);
    
    // Extract the actual data from the WebSocket message structure
    const eventData = data.data || data;
    
    // Show success notification
    toast.success(`Successfully purchased ${eventData.purchase?.playerName}!`, {
      duration: 5000,
      icon: '🎉'
    });
    
    // Refresh available players to remove the purchased player
    if (selectedTournament) {
      fetchAvailablePlayers(selectedTournament.id);
    }
  }, [selectedTournament]);

  // WebSocket connection and event registration
  useEffect(() => {
    if (selectedTournament && user) {
      // Get token from localStorage (AuthContext stores it there)
      const token = localStorage.getItem('token');
      console.log('🔌 WebSocket connection attempt:', {
        tournamentId: selectedTournament.id,
        hasToken: !!token,
        userId: user.id
      });
      
      if (token) {
        // Connect to WebSocket for the selected tournament with user ID
        websocketService.connect(selectedTournament.id, token, user.id);
        
        // Register event handlers
        websocketService.on('WALLET_BALANCE_UPDATED', handleWalletBalanceUpdated);
        websocketService.on('PLAYER_AVAILABILITY_UPDATED', handlePlayerAvailabilityUpdated);
        websocketService.on('PURCHASE_CONFIRMED', handlePurchaseConfirmed);
        websocketService.on('TOURNAMENT_INFO_UPDATED', handleTournamentInfoUpdated);
        websocketService.on('LEADERBOARD_UPDATED', handleLeaderboardUpdated);
        
        // Register connection status callback
        const handleConnectionStatusChange = (connected) => {
          setIsWebSocketConnected(connected);
        };
        websocketService.onConnectionStatusChange(handleConnectionStatusChange);
        
        return () => {
          // Cleanup event handlers
          websocketService.off('WALLET_BALANCE_UPDATED', handleWalletBalanceUpdated);
          websocketService.off('PLAYER_AVAILABILITY_UPDATED', handlePlayerAvailabilityUpdated);
          websocketService.off('PURCHASE_CONFIRMED', handlePurchaseConfirmed);
          websocketService.off('TOURNAMENT_INFO_UPDATED', handleTournamentInfoUpdated);
          websocketService.off('LEADERBOARD_UPDATED', handleLeaderboardUpdated);
          websocketService.offConnectionStatusChange(handleConnectionStatusChange);
        };
      }
    }
  }, [selectedTournament, user, handleWalletBalanceUpdated, handlePlayerAvailabilityUpdated, handlePurchaseConfirmed, handleTournamentInfoUpdated]);

  // Cleanup WebSocket connection on component unmount
  useEffect(() => {
    return () => {
      websocketService.disconnect();
    };
  }, []);

  // Handle auto-purchase when redirected from homepage
  useEffect(() => {
    if (location.state?.autoSelectPlayer && selectedTournament && availablePlayers.length > 0) {
      const { golferId, player, tournamentId } = location.state.autoSelectPlayer;
      
      console.log('🎯 Auto-purchase triggered from homepage:', {
        golferId,
        playerName: player?.golfer?.name,
        tournamentId,
        currentTournamentId: selectedTournament.id
      });
      
      // If tournament ID matches or no specific tournament, trigger purchase
      if (!tournamentId || tournamentId === selectedTournament.id) {
        // Small delay to ensure everything is loaded
        setTimeout(() => {
          handlePurchasePlayer(golferId, player);
        }, 500);
        
        // Clear the state to prevent re-triggering on refresh
        navigate('/betting', { replace: true, state: {} });
      }
    }
  }, [location.state, selectedTournament, availablePlayers]);

  const fetchActiveTournaments = async () => {
    try {
      const response = await api.get('/tournaments');
      const allTournaments = response.data?.data || [];
      const tournaments = allTournaments.filter(t => t.status === 'ACTIVE' || t.status === 'LIVE');
      setTournaments(tournaments);
      if (tournaments.length > 0) {
        setSelectedTournament(tournaments[0]);
      }
    } catch (error) {
      console.error('Error fetching tournaments:', error);
      toast.error('Failed to load tournaments');
      setTournaments([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailablePlayers = async (tournamentId) => {
    try {
      const response = await api.get(`/bets/tournaments/${tournamentId}/available-players`);
      setAvailablePlayers(response.data.data.players || []);
    } catch (error) {
      console.error('Error fetching available players:', error);
      toast.error('Failed to load available players');
    }
  };


  const fetchWalletBalance = async () => {
    try {
      const response = await walletService.getBalance();
      if (response.success && response.data) {
        setWalletBalance(parseFloat(response.data.balance) || 0);
      } else {
        console.warn('Wallet service unavailable, setting balance to 0');
        setWalletBalance(0);
      }
    } catch (error) {
      console.error('Error fetching wallet balance:', error);
      toast.error('Unable to load wallet balance');
      setWalletBalance(0);
    }
  };

  const fetchTierSettings = async () => {
    try {
      const response = await api.get('/tiers');
      if (response.data?.success && response.data?.data) {
        // Convert array to object for easier lookup
        const tierMap = {};
        response.data.data.forEach(tier => {
          tierMap[tier.tier] = tier.defaultPrice;
        });
        setTierSettings(tierMap);
      }
    } catch (error) {
      console.error('Error fetching tier settings:', error);
      // Fallback to default prices if API fails
      setTierSettings({
        'TIER_A': 250,
        'TIER_B': 200,
        'TIER_C': 150,
        'TIER_D': 100
      });
    }
  };

  // Memoized price calculation to prevent excessive function calls
  const playerPrices = useMemo(() => {
    const prices = new Map();
    availablePlayers.forEach(player => {
      const tier = player.tier || player.golfer?.tier;
      let price;
      
      if (tierSettings[tier]) {
        price = parseFloat(tierSettings[tier]);
      } else {
        const fallbackPrices = {
          'TIER_A': 250,
          'TIER_B': 200,
          'TIER_C': 150,
          'TIER_D': 100
        };
        price = fallbackPrices[tier] || 100;
      }
      
      prices.set(player.golfer.id, price);
    });
    return prices;
  }, [availablePlayers, tierSettings]);

  const getTierPrice = (player) => {
    return playerPrices.get(player.golfer.id) || 100;
  };

  const getTierColor = (tier) => {
    const colors = {
      'TIER_A': 'tier-a',
      'TIER_B': 'tier-b',
      'TIER_C': 'tier-c',
      'TIER_D': 'tier-d',
      // Fallback for single letter tiers
      'A': 'tier-a',
      'B': 'tier-b',
      'C': 'tier-c',
      'D': 'tier-d'
    };
    return colors[tier] || 'tier-b';
  };

  const formatTierText = (tier) => {
    if (!tier) return 'Tier D';
    if (tier.startsWith('TIER_')) {
      return tier.replace('TIER_', 'Tier ');
    }
    return `Tier ${tier}`;
  };

  const handlePurchasePlayer = async (golferId, player) => {
    const price = getTierPrice(player);
    
    console.log('🛒 Purchase attempt:', {
      golferId,
      playerName: player.golfer.name,
      price,
      walletBalance,
      sufficientFunds: walletBalance >= price
    });
    
    if (walletBalance < price) {
      console.log('❌ Insufficient funds:', { walletBalance, price, difference: price - walletBalance });
      toast.error('Insufficient funds. Please add money to your wallet.');
      // Redirect to buy credit page with return URL
      navigate(`/buy-credit?returnTo=${encodeURIComponent(window.location.pathname)}`);
      return;
    }

    setPurchaseLoading(golferId);
    try {
      const requestData = {
        golferId,
        amount: price
      };
      const requestUrl = `/bets/tournaments/${selectedTournament.id}/purchase-player`;
      
      console.log('📤 Sending purchase request:', {
        url: requestUrl,
        data: requestData,
        tournamentId: selectedTournament.id
      });
      
      const response = await api.post(requestUrl, requestData);

      toast.success('Player purchased successfully!');
      
      // WebSocket events will handle real-time updates
      // No need to manually refresh data
      
    } catch (error) {
      console.error('❌ Error purchasing player:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      console.error('❌ Error headers:', error.response?.headers);
      
      const message = error.response?.data?.message || error.response?.data?.error || 'Failed to purchase player';
      toast.error(message);
    } finally {
      setPurchaseLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <div className="live-betting-loading-spinner">
            <div className="live-betting-spinner"></div>
          </div>
          <p className="loading-text">Loading active tournaments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="live-betting-layout">
      {/* Main Content - Tournament Live Events */}
      <div className="live-betting-main-content">
        <div className="live-betting-header">
          <div className="live-betting-title-section">
            <div className="live-betting-title-left">
              <h1 className="live-betting-title">Live Betting</h1>
              <p className="live-betting-subtitle">Purchase exclusive ownership of golfers' scores for active tournaments</p>
            </div>
            <div className="wallet-section-header">
              <div className="wallet-info">
                <span className="wallet-label">Balance</span>
                <span className="wallet-balance">${walletBalance.toFixed(2)}</span>
              </div>
              <div className="connection-status">
                <div className={`connection-dot ${isWebSocketConnected ? 'connected' : 'disconnected'}`}></div>
                <span className="connection-text">
                  {isWebSocketConnected ? 'Live' : 'Offline'}
                </span>
              </div>
              {showAddFundsButton && (
                <button
                  onClick={() => navigate(`/buy-credit?returnTo=${encodeURIComponent(window.location.pathname)}`)}
                  className="add-funds-button"
                >
                  Add Funds
                </button>
              )}
            </div>
          </div>
        </div>
        
        <TournamentLiveEvents 
          selectedTournament={selectedTournament}
          userId={user?.id}
          onRefreshAvailablePlayers={fetchAvailablePlayers}
          onRefreshWallet={fetchWalletBalance}
        />
      </div>

      {/* Right Sidebar - Tournament Selection + Available Players */}
      <div className="live-betting-right-sidebar">
        {/* Tournament Selection at Top */}
        <div className="tournament-selection-section">
          <h3 className="tournament-selection-title">Select Tournament</h3>
          <div className="tournament-list-vertical">
            {tournaments.map((tournament) => (
              <div
                key={tournament.id}
                className={`tournament-card-sidebar ${
                  selectedTournament?.id === tournament.id ? 'selected' : ''
                }`}
                onClick={() => setSelectedTournament(tournament)}
              >
                <div className="tournament-status-bar">
                  <span className={`tournament-status ${
                    (tournament.status === 'ACTIVE' || tournament.status === 'LIVE') ? 'active' : 'draft'
                  }`}>
                    {tournament.status}
                  </span>
                </div>
                <h4 className="tournament-name-sidebar">{tournament.name}</h4>
              </div>
            ))}
          </div>
        </div>

        {/* Available Players */}
        {selectedTournament ? (
          <PlayersTable 
            players={availablePlayers}
            title="Available Players"
            showCount={true}
            onPurchasePlayer={handlePurchasePlayer}
            purchaseLoading={purchaseLoading}
            walletBalance={walletBalance}
            isTournamentStarted={isTournamentStarted}
            showBuyButton={true}
            linkToBetting={false}
            tournament={selectedTournament}
          />
        ) : (
          <div className="empty-state no-tournament-selected">
            <p>Select a tournament to view available players</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveBetting;